import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Upload,
  FileText,
  File,
  Music,
  Image,
  Globe,
  Users,
  Lock,
  Building,
  Check,
  X,
  Trash2,
  Eye,
  Filter,
  Plus,
  AlertCircle,
  Loader2,
  MapPin,
  Pause,
  Play,
  Edit3,
  UserCircle
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const CONFIDENTIALITY_OPTIONS = [
  // Nivel 1: Solo equipo Manantial
  { value: 'department', label: 'Solo Departamento', icon: Building, color: 'red', description: 'Solo el departamento seleccionado', group: 'equipo' },
  { value: 'internal', label: 'Solo Equipo Manantial', icon: Lock, color: 'orange', description: 'Solo empleados de Manantial', group: 'equipo' },
  // Nivel 2: Franquiciados (separado)
  { value: 'franchisees', label: 'Franquiciados', icon: Users, color: 'blue', description: 'Franquiciados y equipo Manantial (excluye lugares y clientes)', group: 'franquiciados' },
  // Nivel 3: Lugares (separado)
  { value: 'lugares', label: 'Lugares', icon: MapPin, color: 'teal', description: 'Lugares, franquiciados y equipo (excluye clientes finales)', group: 'lugares' },
  // Nivel 4: Clientes finales (separado)
  { value: 'public', label: 'Clientes finales', icon: UserCircle, color: 'green', description: 'Todos pueden ver incluyendo clientes finales', group: 'clientes' }
];

const FILE_TYPE_ICONS = {
  text: FileText,
  pdf: FileText,
  word: File,
  excel: File,
  audio: Music,
  image: Image,
  video: File,
  youtube: File,
  drive: File,
  url: Globe
};

const SOURCE_TYPES = [
  { value: 'text', label: 'Texto', icon: FileText, description: 'Escribe o pega texto directamente' },
  { value: 'url', label: 'Enlace web', icon: Globe, description: 'URL de pagina web' },
  { value: 'youtube', label: 'YouTube', icon: File, description: 'Enlace de video de YouTube' },
  { value: 'drive', label: 'Google Drive', icon: File, description: 'Enlace de documento en Drive' },
  { value: 'pdf', label: 'PDF', icon: FileText, description: 'Documento PDF' },
  { value: 'word', label: 'Word', icon: File, description: 'Documento Word (.doc, .docx)' },
  { value: 'excel', label: 'Excel', icon: File, description: 'Hoja de calculo (.xls, .xlsx)' },
  { value: 'image', label: 'Imagen', icon: Image, description: 'Imagen con texto (OCR)' },
  { value: 'audio', label: 'Audio', icon: Music, description: 'Audio para transcribir' }
];

export default function KnowledgeManager({ departments = [] }) {
  // Verificar permisos (solo revisor+ puede alimentar IA)
  const { canFeedAI, user } = useAuth();
  const canEdit = canFeedAI();

  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filter, setFilter] = useState({ department: 'all', confidentiality: 'all', status: 'all' });
  const [stats, setStats] = useState(null);

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    source_url: '',
    department_id: 'global',
    confidentiality: 'internal',
    file_type: 'text'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadKnowledge();
    loadStats();
  }, [filter]);

  const loadKnowledge = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.department !== 'all') params.department_id = filter.department;
      if (filter.confidentiality !== 'all') params.confidentiality = filter.confidentiality;
      if (filter.status !== 'all') params.status = filter.status;

      const response = await api.get('/api/knowledge', { params });
      setKnowledge(response.data.knowledge || []);
    } catch (err) {
      console.error('Error loading knowledge:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/api/knowledge/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      let response;

      // Si hay archivo, usar FormData
      if (formData.file) {
        const formDataToSend = new FormData();
        formDataToSend.append('file', formData.file);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('content', formData.content || '');
        formDataToSend.append('department_id', formData.department_id);
        formDataToSend.append('confidentiality', formData.confidentiality);
        formDataToSend.append('file_type', formData.file_type);

        response = await api.post('/api/knowledge/upload', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Sin archivo, enviar JSON normal
        response = await api.post('/api/knowledge', formData);
      }

      setShowUploadModal(false);
      setFormData({
        title: '',
        content: '',
        source_url: '',
        department_id: 'global',
        confidentiality: 'internal',
        file_type: 'text',
        file: null
      });
      loadKnowledge();
      loadStats();

      // Show success message
      if (response.data.extracted_chars) {
        alert(`Conocimiento guardado. Se extrajeron ${response.data.extracted_chars} caracteres del documento.`);
      } else {
        alert('Conocimiento guardado correctamente.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReview = async (id, action) => {
    try {
      await api.put(`/api/knowledge/${id}/review`, { action });
      loadKnowledge();
      loadStats();
    } catch (err) {
      console.error('Error reviewing:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este conocimiento?')) return;
    try {
      await api.delete(`/api/knowledge/${id}`);
      loadKnowledge();
      loadStats();
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleTogglePause = async (id) => {
    try {
      await api.put(`/api/knowledge/${id}/toggle-pause`);
      loadKnowledge();
    } catch (err) {
      console.error('Error toggling pause:', err);
    }
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowViewModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      content: item.content || '',
      source_url: item.source_url || '',
      department_id: item.department_id || 'global',
      confidentiality: item.confidentiality || 'internal',
      file_type: item.file_type || 'text'
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await api.put(`/api/knowledge/${selectedItem.id}`, formData);
      setShowEditModal(false);
      setSelectedItem(null);
      setFormData({
        title: '',
        content: '',
        source_url: '',
        department_id: 'global',
        confidentiality: 'internal',
        file_type: 'text'
      });
      loadKnowledge();
      loadStats();
      alert('Conocimiento actualizado correctamente.');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const getConfidentialityBadge = (level) => {
    const option = CONFIDENTIALITY_OPTIONS.find(o => o.value === level);
    if (!option) return null;
    const Icon = option.icon;
    const colors = {
      green: 'bg-green-100 text-green-700',
      blue: 'bg-blue-100 text-blue-700',
      orange: 'bg-orange-100 text-orange-700',
      red: 'bg-red-100 text-red-700',
      teal: 'bg-teal-100 text-teal-700'
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${colors[option.color]}`}>
        <Icon className="w-3 h-3 mr-1" />
        {option.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado'
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <BookOpen className="w-7 h-7 mr-3 text-purple-600" />
            Base de Conocimiento IA
          </h2>
          <p className="text-gray-500 mt-1">Gestiona la informacion que Xavia utiliza para responder</p>
        </div>
{canEdit ? (
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5 mr-2" />
            Añadir Conocimiento
          </button>
        ) : (
          <div className="flex items-center px-4 py-2 bg-gray-100 text-gray-500 rounded-lg">
            <Lock className="w-4 h-4 mr-2" />
            Solo revisor o superior
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
            <p className="text-sm text-yellow-600">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <p className="text-sm text-green-600">Aprobados</p>
            <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <p className="text-sm text-red-600">Rechazados</p>
            <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border mb-6">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={filter.department}
            onChange={(e) => setFilter({ ...filter, department: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">Todos los departamentos</option>
            <option value="global">Global</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
          <select
            value={filter.confidentiality}
            onChange={(e) => setFilter({ ...filter, confidentiality: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">Toda confidencialidad</option>
            {CONFIDENTIALITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="approved">Aprobado</option>
            <option value="rejected">Rechazado</option>
          </select>
        </div>
      </div>

      {/* Knowledge List */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" />
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : knowledge.length === 0 ? (
          <div className="p-8 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No hay conocimiento registrado</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-3 text-purple-600 hover:underline"
            >
              Añadir el primero
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titulo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departamento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alcance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creado por</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {knowledge.map((item) => {
                const FileIcon = FILE_TYPE_ICONS[item.file_type] || FileText;
                return (
                  <tr key={item.id} className={`hover:bg-gray-50 ${item.paused ? 'opacity-60 bg-gray-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <FileIcon className={`w-5 h-5 mr-2 ${item.paused ? 'text-gray-300' : 'text-gray-400'}`} />
                        <div>
                          <div className="flex items-center">
                            <p className={`font-medium ${item.paused ? 'text-gray-500' : 'text-gray-800'}`}>{item.title}</p>
                            {item.paused && (
                              <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                                Pausado
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate max-w-xs">{item.content?.substring(0, 50)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {item.department_id === 'global' ? 'Global' :
                          departments.find(d => d.id === item.department_id)?.name || item.department_id}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {getConfidentialityBadge(item.confidentiality)}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{item.submitted_by_name}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        {/* Botón pausar/reanudar */}
                        <button
                          onClick={() => handleTogglePause(item.id)}
                          className={`p-1.5 rounded transition ${
                            item.paused
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-yellow-600 hover:bg-yellow-50'
                          }`}
                          title={item.paused ? 'Reanudar' : 'Pausar'}
                        >
                          {item.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                        </button>
                        {/* Botón ver documento */}
                        <button
                          onClick={() => handleView(item)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
                          title="Ver documento"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {/* Botón editar */}
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition"
                          title="Editar"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {/* Acciones de revisión */}
                        {item.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleReview(item.id, 'approve')}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Aprobar"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReview(item.id, 'reject')}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Rechazar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <Upload className="w-5 h-5 mr-2 text-purple-600" />
                Añadir Conocimiento para Xavia
              </h3>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {error}
                </div>
              )}

              {/* Tipo de fuente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de contenido</label>
                <div className="grid grid-cols-3 gap-2">
                  {SOURCE_TYPES.map(type => {
                    const Icon = type.icon;
                    const isSelected = formData.file_type === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, file_type: type.value, content: '', source_url: '' })}
                        className={`flex flex-col items-center p-3 border rounded-lg transition ${
                          isSelected ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <Icon className="w-5 h-5 mb-1" />
                        <span className="text-xs font-medium">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {SOURCE_TYPES.find(t => t.value === formData.file_type)?.description}
                </p>
              </div>

              {/* Titulo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titulo</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Ej: Politica de devoluciones"
                  required
                />
              </div>

              {/* Campo segun tipo de fuente */}
              {formData.file_type === 'text' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contenido</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    rows={6}
                    placeholder="Escribe o pega la informacion que Xavia debe conocer..."
                    required
                  />
                </div>
              )}

              {['url', 'youtube', 'drive'].includes(formData.file_type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.file_type === 'youtube' ? 'URL del video de YouTube' :
                     formData.file_type === 'drive' ? 'URL del documento en Google Drive' :
                     'URL de la pagina web'}
                  </label>
                  <input
                    type="url"
                    value={formData.source_url}
                    onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder={
                      formData.file_type === 'youtube' ? 'https://www.youtube.com/watch?v=...' :
                      formData.file_type === 'drive' ? 'https://docs.google.com/document/d/...' :
                      'https://ejemplo.com/pagina'
                    }
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.file_type === 'drive' && 'Asegurate de que el documento sea publico o tengas permisos de acceso'}
                    {formData.file_type === 'youtube' && 'Se extraera la transcripcion del video automaticamente'}
                    {formData.file_type === 'url' && 'Se extraera el contenido de la pagina web'}
                  </p>

                  {/* Campo opcional para descripcion adicional */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripcion adicional (opcional)
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows={3}
                      placeholder="Añade contexto o notas sobre este contenido..."
                    />
                  </div>
                </div>
              )}

              {['pdf', 'word', 'excel', 'image', 'audio'].includes(formData.file_type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subir archivo</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition cursor-pointer">
                    <input
                      type="file"
                      id="fileUpload"
                      className="hidden"
                      accept={
                        formData.file_type === 'pdf' ? '.pdf' :
                        formData.file_type === 'word' ? '.doc,.docx' :
                        formData.file_type === 'excel' ? '.xls,.xlsx,.csv' :
                        formData.file_type === 'image' ? 'image/*' :
                        formData.file_type === 'audio' ? 'audio/*' : '*'
                      }
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setFormData({ ...formData, file: file, content: `Archivo: ${file.name}` });
                        }
                      }}
                    />
                    <label htmlFor="fileUpload" className="cursor-pointer">
                      <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">
                        {formData.file ? formData.file.name : 'Haz clic para seleccionar un archivo'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formData.file_type === 'pdf' && 'PDF (max 10MB)'}
                        {formData.file_type === 'word' && 'Word .doc, .docx (max 10MB)'}
                        {formData.file_type === 'excel' && 'Excel .xls, .xlsx, .csv (max 10MB)'}
                        {formData.file_type === 'image' && 'Imagen con texto - se extraera con OCR (max 5MB)'}
                        {formData.file_type === 'audio' && 'Audio - se transcribira automaticamente (max 25MB)'}
                      </p>
                    </label>
                  </div>

                  {/* Campo opcional para descripcion */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripcion adicional (opcional)
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      rows={3}
                      placeholder="Añade contexto o notas sobre este archivo..."
                    />
                  </div>
                </div>
              )}

              {/* Departamento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Departamento destino</label>
                <select
                  value={formData.department_id}
                  onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="global">Global (todos los chats)</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              {/* Confidencialidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ¿Quien puede acceder a esta informacion?
                </label>
                <div className="space-y-2">
                  {CONFIDENTIALITY_OPTIONS.map(option => {
                    const Icon = option.icon;
                    const isSelected = formData.confidentiality === option.value;
                    return (
                      <label
                        key={option.value}
                        className={`flex items-start p-3 border rounded-lg cursor-pointer transition ${
                          isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="confidentiality"
                          value={option.value}
                          checked={isSelected}
                          onChange={(e) => setFormData({ ...formData, confidentiality: e.target.value })}
                          className="sr-only"
                        />
                        <Icon className={`w-5 h-5 mr-3 mt-0.5 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                            {option.label}
                          </p>
                          <p className="text-sm text-gray-500">{option.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Botones */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <Eye className="w-5 h-5 mr-2 text-blue-600" />
                Ver Documento
              </h3>
              <button onClick={() => { setShowViewModal(false); setSelectedItem(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Info del documento */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-xl font-bold text-gray-800">{selectedItem.title}</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Creado por {selectedItem.submitted_by_name} • {new Date(selectedItem.created_at).toLocaleDateString('es-ES')}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {getConfidentialityBadge(selectedItem.confidentiality)}
                  {getStatusBadge(selectedItem.status)}
                  {selectedItem.paused && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                      Pausado
                    </span>
                  )}
                </div>
              </div>

              {/* URL si existe */}
              {selectedItem.source_url && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">URL de origen:</p>
                  <a href={selectedItem.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                    {selectedItem.source_url}
                  </a>
                </div>
              )}

              {/* Contenido */}
              <div className="border rounded-lg">
                <div className="p-3 bg-gray-50 border-b">
                  <p className="text-sm font-medium text-gray-700">Contenido</p>
                </div>
                <div className="p-4 whitespace-pre-wrap text-gray-700 max-h-96 overflow-y-auto">
                  {selectedItem.content || 'Sin contenido'}
                </div>
              </div>

              {/* Metadatos */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Tipo de archivo</p>
                  <p className="font-medium">{selectedItem.file_type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Departamento</p>
                  <p className="font-medium">
                    {selectedItem.department_id === 'global' ? 'Global' :
                      departments.find(d => d.id === selectedItem.department_id)?.name || selectedItem.department_id}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end p-4 border-t">
              <button
                onClick={() => { setShowViewModal(false); setSelectedItem(null); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center">
                <Edit3 className="w-5 h-5 mr-2 text-purple-600" />
                Editar Conocimiento
              </h3>
              <button onClick={() => { setShowEditModal(false); setSelectedItem(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              {error && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {error}
                </div>
              )}

              {/* Titulo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titulo</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              {/* Contenido */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenido</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows={8}
                />
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL de origen (opcional)</label>
                <input
                  type="url"
                  value={formData.source_url}
                  onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Departamento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Departamento destino</label>
                <select
                  value={formData.department_id}
                  onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="global">Global (todos los chats)</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              {/* Confidencialidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ¿Quien puede acceder a esta informacion?
                </label>
                <div className="space-y-2">
                  {CONFIDENTIALITY_OPTIONS.map(option => {
                    const Icon = option.icon;
                    const isSelected = formData.confidentiality === option.value;
                    return (
                      <label
                        key={option.value}
                        className={`flex items-start p-3 border rounded-lg cursor-pointer transition ${
                          isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="confidentiality_edit"
                          value={option.value}
                          checked={isSelected}
                          onChange={(e) => setFormData({ ...formData, confidentiality: e.target.value })}
                          className="sr-only"
                        />
                        <Icon className={`w-5 h-5 mr-3 mt-0.5 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                            {option.label}
                          </p>
                          <p className="text-sm text-gray-500">{option.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Botones */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedItem(null); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Guardar cambios
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
